import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Toggle } from './ui/toggle';
import { Upload, X, Camera, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import PricingSection from './PricingSection';
import HiveTypeConfig, { HIVE_TYPES, StackRow, normalizeBoxTypes } from './hive/HiveTypeConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddHiveDialog = ({ open, onClose, onAdd, existingHives, trapData }) => {
  const [yards, setYards] = useState([]);
  const [swarmTraps, setSwarmTraps] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isSplit, setIsSplit] = useState(false);
  const [splitFromHive, setSplitFromHive] = useState('');
  const [splitType, setSplitType] = useState('');
  const [splitTypeOther, setSplitTypeOther] = useState('');
  const [hiveType, setHiveType] = useState('');
  const [hivePricing, setHivePricing] = useState({});
  const [defaultPrices, setDefaultPrices] = useState({});
  const [originPrice, setOriginPrice] = useState(0);
  const [adminBeeVarieties, setAdminBeeVarieties] = useState([]);
  const [adminOriginTypes, setAdminOriginTypes] = useState([]);
  const [adminComponents, setAdminComponents] = useState([]);
  const [formData, setFormData] = useState({
    hive_number: '',
    nickname: '',
    yard: '',
    location: '',
    notes: '',
    hive_image: '',
    bee_variety: '',
    origin_type: '',
    swarm_source: '',
    swarm_trap_id: '',
    origin_seller: '',
    origin_date: '',
    origin_location: '',
    origin_price: 0,
    is_split: false,
    split_from_hive: '',
    split_type: '',
    split_type_other: '',
    hive_type: '',
    hive_config: {
      // Langstroth
      langstroth_deep_brood_size: '10',
      langstroth_deep_brood_qty: 1,
      langstroth_deep_super_size: '10',
      langstroth_deep_super_qty: 0,
      langstroth_ideal_super_size: '10',
      langstroth_ideal_super_qty: 0,
      // Top Bar
      top_bar_frames: '',
      // Horizontal
      horizontal_frames: '',
      horizontal_entrances: '',
      horizontal_colonies: '',
      // Layens
      layens_frames: '',
      // NUC
      nuc_frames: '',
      // Flow Hive
      flow_deep_brood_size: '10',
      flow_deep_brood_qty: 1,
      flow_super_size: '6',
      flow_super_qty: 0,  // Optional - 0 means no Flow Super yet
      // National
      national_variant: '1',
      national_deep_size: '11',
      national_deep_qty: 0,
      national_shallow_size: '10',
      national_shallow_qty: 0,
      // Keepers
      keepers_type: 'one_queen',
      keepers_langstroth_deep_qty: 0,
      keepers_medium_nuc_qty: 0,
      keepers_super_deep_qty: 0,
      // Warre
      warre_boxes: 0,
      // Queen Mating
      queen_mating_frames: '',
      // Other
      other_description: '',
    },
    hive_components: {
      hive_entrance_count: 1,
    },
  });
  const [selectedFrameTypes, setSelectedFrameTypes] = useState([]);
  const [selectedFoundationTypes, setSelectedFoundationTypes] = useState([]);
  const [frameQuantities, setFrameQuantities] = useState({});
  const [foundationQuantities, setFoundationQuantities] = useState({});
  const [allFramesSelected, setAllFramesSelected] = useState(false);
  const [allFoundationsSelected, setAllFoundationsSelected] = useState(false);
  const [adminHiveTypes, setAdminHiveTypes] = useState([]);
  const [userHiveTypes, setUserHiveTypes] = useState([]);
  const [hiveTemplates, setHiveTemplates] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [boxConfig, setBoxConfig] = useState({});
  const [copiedFromHive, setCopiedFromHive] = useState('');

  useEffect(() => {
    if (open) {
      setCopiedFromHive('');
      fetchYards();
      fetchSwarmTraps();
      fetchAdminConfig();
      fetchHiveTemplates();
      fetchDefaultPrices();
      fetchAdminHiveTypes();
    }
  }, [open]);

  const fetchDefaultPrices = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/hive-costs`, { headers });
      const prices = response.data.prices || {};
      setDefaultPrices(prices);
      // Don't pre-populate hivePricing - defaults are applied dynamically in PricingSection
    } catch (error) {
    }
  };

  const fetchYards = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/yards`, { headers });
      setYards(response.data);
    } catch (error) {
    }
  };

  const fetchSwarmTraps = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/swarm-traps`, { headers });
      setSwarmTraps(response.data);
    } catch (error) {
    }
  };

  const fetchAdminConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [varietiesRes, originsRes, componentsRes] = await Promise.all([
        fetch(`${API}/config/bee_varieties`, { headers }),
        fetch(`${API}/config/origin_types`, { headers }),
        fetch(`${API}/config/admin_components`, { headers })
      ]);
      if (varietiesRes.ok) {
        const data = await varietiesRes.json();
        // Handle both array and {items: []} response formats
        setAdminBeeVarieties(Array.isArray(data) ? data : (data.items || []));
      }
      if (originsRes.ok) {
        const data = await originsRes.json();
        setAdminOriginTypes(Array.isArray(data) ? data : (data.items || []));
      }
      if (componentsRes.ok) {
        const data = await componentsRes.json();
        setAdminComponents(Array.isArray(data) ? data : (data.items || []));
      }
    } catch (error) {
    }
  };

  const fetchHiveTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/hive-templates`, { headers });
      setHiveTemplates(response.data || []);
    } catch (error) {
    }
  };

  const fetchAdminHiveTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [adminRes, userRes] = await Promise.all([
        fetch(`${API}/config/hive_types`, { headers }).catch(() => null),
        fetch(`${API}/config/user-hive-types`, { headers }).catch(() => null)
      ]);
      if (adminRes?.ok) {
        const data = await adminRes.json();
        setAdminHiveTypes(Array.isArray(data) ? data : (data.items || []));
      }
      if (userRes?.ok) {
        const data = await userRes.json();
        setUserHiveTypes(Array.isArray(data) ? data : []);
      }
    } catch (error) {
    }
  };

  // Frame & Foundation helpers
  const getAvailableFrameTypes = () => {
    const cfg = adminHiveTypes.find(ht => {
      const htKey = ht.name?.toLowerCase().replace(/\s+/g, '_');
      return htKey === hiveType || ht.name === hiveType || ht.name?.toLowerCase() === hiveType;
    }) || userHiveTypes.find(ht => `custom_${ht.id}` === hiveType);
    return cfg?.frame_types || ['Plastic', 'Wooden', 'Specialty', 'Flow Frames'];
  };

  const getAvailableFoundationTypes = () => {
    const cfg = adminHiveTypes.find(ht => {
      const htKey = ht.name?.toLowerCase().replace(/\s+/g, '_');
      return htKey === hiveType || ht.name === hiveType || ht.name?.toLowerCase() === hiveType;
    }) || userHiveTypes.find(ht => `custom_${ht.id}` === hiveType);
    return cfg?.foundation_types || ['Wax', 'Plastic', 'Waxed Plastic', 'Strips', 'Foundationless'];
  };

  // Get current hive type configuration (admin or user custom)
  const getCurrentHiveTypeConfig = () => {
    return adminHiveTypes.find(ht => {
      const htKey = ht.name?.toLowerCase().replace(/\s+/g, '_');
      return htKey === hiveType || ht.name === hiveType || ht.name?.toLowerCase() === hiveType;
    }) || userHiveTypes.find(ht => `custom_${ht.id}` === hiveType);
  };

  // Update box configuration
  const updateBoxConfig = (boxTypeName, updates) => {
    setBoxConfig(prev => ({
      ...prev,
      [boxTypeName]: updates
    }));
  };

  const handleFrameTypeToggle = (frameType) => {
    if (selectedFrameTypes.includes(frameType)) {
      setSelectedFrameTypes(selectedFrameTypes.filter(f => f !== frameType));
      const newQty = { ...frameQuantities };
      delete newQty[frameType];
      setFrameQuantities(newQty);
    } else {
      setSelectedFrameTypes([...selectedFrameTypes, frameType]);
      setFrameQuantities({ ...frameQuantities, [frameType]: 0 });
    }
    setAllFramesSelected(false);
  };

  const handleFoundationTypeToggle = (foundationType) => {
    if (selectedFoundationTypes.includes(foundationType)) {
      setSelectedFoundationTypes(selectedFoundationTypes.filter(f => f !== foundationType));
      const newQty = { ...foundationQuantities };
      delete newQty[foundationType];
      setFoundationQuantities(newQty);
    } else {
      setSelectedFoundationTypes([...selectedFoundationTypes, foundationType]);
      setFoundationQuantities({ ...foundationQuantities, [foundationType]: 0 });
    }
    setAllFoundationsSelected(false);
  };

  const handleAllFramesToggle = () => {
    const available = getAvailableFrameTypes();
    if (allFramesSelected) {
      setSelectedFrameTypes([]);
      setFrameQuantities({});
      setAllFramesSelected(false);
    } else {
      setSelectedFrameTypes(available);
      const newQty = {};
      available.forEach(f => newQty[f] = frameQuantities[f] || 0);
      setFrameQuantities(newQty);
      setAllFramesSelected(true);
    }
  };

  const handleAllFoundationsToggle = () => {
    const available = getAvailableFoundationTypes();
    if (allFoundationsSelected) {
      setSelectedFoundationTypes([]);
      setFoundationQuantities({});
      setAllFoundationsSelected(false);
    } else {
      setSelectedFoundationTypes(available);
      const newQty = {};
      available.forEach(f => newQty[f] = foundationQuantities[f] || 0);
      setFoundationQuantities(newQty);
      setAllFoundationsSelected(true);
    }
  };

  const updateHiveComponents = (key, value) => {
    setFormData(prev => ({
      ...prev,
      hive_components: { ...prev.hive_components, [key]: parseInt(value) || 0 }
    }));
  };

  const isComponentActive = (componentName) => {
    if (adminComponents.length === 0) return true; // Show all if config not loaded
    const normalizedName = componentName.toLowerCase().replace(/[_\s]+/g, ' ').trim();
    return adminComponents.some(c => {
      const adminName = c.name.toLowerCase().replace(/[_\s]+/g, ' ').trim();
      return adminName === normalizedName ||
             c.name.toLowerCase() === componentName.toLowerCase() ||
             (normalizedName.includes('migration') && adminName.includes('migratory')) ||
             (normalizedName.includes('migratory') && adminName.includes('migration')) ||
             (normalizedName.includes('screened') && adminName.includes('screened'));
    });
  };

  // Build dynamic component sections from admin_components
  const renderDynamicComponentSections = () => {
    const sections = {};
    const sectionLabels = {
      lids_covers: { label: 'Lids & Covers', icon: '🎩', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
      bottom_boards_stands: { label: 'Bottom Boards & Stands', icon: '📦', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' },
      feeders: { label: 'Feeders', icon: '🍯', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800' },
      queen_bee_management: { label: 'Queen Management', icon: '👑', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800' },
      pest_control: { label: 'Pest Control', icon: '🛡️', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800' },
      heavy_accessories: { label: 'Heavy & Accessories', icon: '⚙️', bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-800' },
      miscellaneous: { label: 'Miscellaneous', icon: '📋', bg: 'bg-stone-50', border: 'border-stone-200', text: 'text-stone-800' },
    };

    // Group active components by section
    adminComponents.filter(c => c.is_active !== false).forEach(comp => {
      const section = comp.section || 'miscellaneous';
      if (!sections[section]) sections[section] = [];
      sections[section].push(comp);
    });

    return Object.entries(sections).map(([sectionKey, components]) => {
      const style = sectionLabels[sectionKey] || { 
        label: sectionKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), 
        icon: '✨', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800' 
      };
      return (
        <div key={sectionKey} className={`${style.bg} border ${style.border} rounded-xl p-3`}>
          <h4 className={`text-sm font-semibold ${style.text} mb-2`}>{style.icon} {style.label}</h4>
          <div className="grid grid-cols-2 gap-3">
            {components.map(comp => {
              const compKey = comp.form_key || comp.name.toLowerCase().replace(/\s+/g, '_');
              return (
                <div key={comp.id}>
                  <Label className="text-xs text-stone-600">{comp.name}</Label>
                  <Input type="number" min="0"
                    value={formData.hive_components[compKey] || 0}
                    onChange={(e) => updateHiveComponents(compKey, e.target.value)}
                    className="h-9 mt-1 bg-white text-sm" />
                </div>
              );
            })}
          </div>
        </div>
      );
    });
  };

  const applyTemplate = (template) => {
    if (!template) return;
    
    setHiveType(template.hive_type);
    const config = { ...template.configuration } || {};
    
    // Ensure at least 1 brood box — every hive needs one
    const type = (template.hive_type || '').toLowerCase();
    if (type === 'langstroth' || type.includes('langstroth')) {
      if (!config.langstroth_deep_brood_qty) config.langstroth_deep_brood_qty = 1;
    } else if (type === 'flow' || type.includes('flow')) {
      if (!config.flow_deep_brood_qty) config.flow_deep_brood_qty = 1;
    } else if (type === 'national') {
      if (!config.national_deep_qty) config.national_deep_qty = 1;
    }
    
    setFormData(prev => ({
      ...prev,
      hive_config: {
        ...prev.hive_config,
        ...config
      }
    }));
    
    // Also update boxConfig for the dynamic box type renderer
    // Template keys like langstroth_deep_brood_qty/size need to map to boxConfig["Deep Brood"] = {qty, size}
    const hiveTypeConfig = adminHiveTypes.find(ht => {
      const htKey = ht.name?.toLowerCase().replace(/\s+/g, '_');
      return htKey === template.hive_type || ht.name?.toLowerCase() === template.hive_type;
    });
    if (hiveTypeConfig?.box_types) {
      const prefix = type.replace(/\s+/g, '_');
      const newBoxConfig = {};
      const boxTypes = hiveTypeConfig.box_types.map(bt => typeof bt === 'string' ? bt : bt.name);
      boxTypes.forEach(boxName => {
        const boxKey = boxName.toLowerCase().replace(/\s+/g, '_');
        const qtyKey = `${prefix}_${boxKey}_qty`;
        const sizeKey = `${prefix}_${boxKey}_size`;
        newBoxConfig[boxName] = {
          qty: config[qtyKey] || 0,
          size: config[sizeKey] || '10',
        };
      });
      setBoxConfig(prev => ({ ...prev, ...newBoxConfig }));
    }
    
    toast.success(`Template "${template.name}" applied`);
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/hive-templates`, {
        name: templateName,
        description: `${hiveType} configuration`,
        hive_type: hiveType,
        configuration: formData.hive_config,
        icon: getTemplateIcon(hiveType)
      }, { headers });
      
      toast.success(`Template "${templateName}" saved!`);
      setShowSaveTemplate(false);
      setTemplateName('');
      fetchHiveTemplates(); // Refresh the list
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const getTemplateIcon = (type) => {
    const icons = {
      langstroth: '📦',
      horizontal: '🪵',
      flow: '🍯',
      top_bar: '🔺',
      nuc: '📋',
      warre: '🏠',
      national: '🇬🇧',
      keepers: '👑',
      layens: '📐',
      queen_mating: '👸'
    };
    return icons[type?.toLowerCase()] || '📦';
  };

  const getNextHiveNumber = () => {
    if (existingHives.length === 0) return 1;
    const maxNumber = Math.max(...existingHives.map((h) => h.hive_number));
    return maxNumber + 1;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const hiveNumber = formData.hive_number || getNextHiveNumber();
    
    // Build hive_stack based on selected hive type - only include relevant fields
    // Resolve custom type ID to friendly name
    let resolvedHiveType = hiveType;
    if (hiveType?.startsWith('custom_')) {
      const userMatch = userHiveTypes.find(ht => `custom_${ht.id}` === hiveType);
      if (userMatch) resolvedHiveType = userMatch.name;
    }
    
    const hive_stack = {
      hive_type: resolvedHiveType,
    };
    
    // Check if hive type uses dynamic box configuration
    const hiveTypeConfig = getCurrentHiveTypeConfig();
    const hasBoxTypes = hiveTypeConfig?.requires_boxes || (hiveTypeConfig?.box_types?.length > 0);
    
    if (hasBoxTypes) {
      hive_stack.box_config = boxConfig;
    } else {
      switch (hiveType) {
      case 'langstroth':
        hive_stack.deep_brood_size = formData.hive_config.langstroth_deep_brood_size || '10';
        hive_stack.deep_brood_qty = formData.hive_config.langstroth_deep_brood_qty || 0;
        hive_stack.deep_super_size = formData.hive_config.langstroth_deep_super_size || '10';
        hive_stack.deep_super_qty = formData.hive_config.langstroth_deep_super_qty || 0;
        hive_stack.ideal_super_size = formData.hive_config.langstroth_ideal_super_size || '10';
        hive_stack.ideal_super_qty = formData.hive_config.langstroth_ideal_super_qty || 0;
        break;
      case 'top_bar':
        hive_stack.top_hive_frames = formData.hive_config.top_bar_frames || '';
        break;
      case 'horizontal':
        hive_stack.horizontal_hive_frames = formData.hive_config.horizontal_frames || '';
        hive_stack.horizontal_hive_entrances = formData.hive_config.horizontal_entrances || '';
        hive_stack.horizontal_hive_colonies = formData.hive_config.horizontal_colonies || '';
        break;
      case 'layens':
        hive_stack.layens_hive_frames = formData.hive_config.layens_frames || '';
        break;
      case 'nuc':
        hive_stack.nuc_frames = formData.hive_config.nuc_frames || '';
        break;
      case 'flow_hive':
        hive_stack.flow_hive_deep_brood_size = formData.hive_config.flow_deep_brood_size || '10';
        hive_stack.flow_hive_deep_brood_qty = formData.hive_config.flow_deep_brood_qty || 0;
        hive_stack.flow_hive_super_size = formData.hive_config.flow_super_size || '6';
        hive_stack.flow_hive_super_qty = formData.hive_config.flow_super_qty || 0;
        break;
      case 'national':
        hive_stack.national_variant = formData.hive_config.national_variant || '1';
        hive_stack.national_deep_size = formData.hive_config.national_deep_size || '11';
        hive_stack.national_deep_qty = formData.hive_config.national_deep_qty || 0;
        hive_stack.national_shallow_size = formData.hive_config.national_shallow_size || '10';
        hive_stack.national_shallow_qty = formData.hive_config.national_shallow_qty || 0;
        break;
      case 'keepers':
        hive_stack.keepers_type = formData.hive_config.keepers_type || 'one_queen';
        hive_stack.keepers_langstroth_deep_qty = formData.hive_config.keepers_langstroth_deep_qty || 0;
        hive_stack.keepers_medium_nuc_qty = formData.hive_config.keepers_medium_nuc_qty || 0;
        hive_stack.keepers_super_deep_qty = formData.hive_config.keepers_super_deep_qty || 0;
        break;
      case 'warre':
        hive_stack.warre_boxes = formData.hive_config.warre_boxes || 0;
        break;
      case 'queen_mating':
        hive_stack.queen_mating_frames = formData.hive_config.queen_mating_frames || '';
        break;
      case 'other':
        hive_stack.other_description = formData.hive_config.other_description || '';
        break;
      default:
        break;
      }
    }
    
    onAdd({
      ...formData,
      hive_number: parseInt(hiveNumber),
      nickname: formData.nickname || '',
      hive_stack: hive_stack,
      box_config: boxConfig,
      hive_pricing: hivePricing,
      hive_components: formData.hive_components,
      origin_price: originPrice,
      frame_types: selectedFrameTypes,
      frame_quantities: frameQuantities,
      foundation_types: selectedFoundationTypes,
      foundation_quantities: foundationQuantities,
    });
    // Reset form
    setFormData({
      hive_number: '',
      nickname: '',
      yard: '',
      location: '',
      notes: '',
      hive_image: '',
      hive_config: {},
    });
    setHiveType('');
    setIsSplit(false);
    setSplitFromHive('');
    setSplitType('');
    setSplitTypeOther('');
    setHivePricing({});
    setOriginPrice(0);
    setBoxConfig({});
    setSelectedFrameTypes([]);
    setSelectedFoundationTypes([]);
    setFrameQuantities({});
    setFoundationQuantities({});
    setAllFramesSelected(false);
    setAllFoundationsSelected(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload);
      setFormData({ ...formData, hive_image: response.data.file_id });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const updateConfig = (key, value) => {
    setFormData({
      ...formData,
      hive_config: { ...formData.hive_config, [key]: value }
    });
  };

  // Render hive-specific configuration options
  const renderHiveConfig = () => {
    const hiveTypeConfig = getCurrentHiveTypeConfig();
    const boxTypes = hiveTypeConfig?.box_types || [];
    if (boxTypes.length > 0) {
      return (
        <HiveTypeConfig
          hiveType={hiveType}
          config={formData.hive_config}
          updateConfig={updateConfig}
          customBoxTypes={boxTypes}
          boxConfig={boxConfig}
          onBoxConfigChange={(name, cfg) => updateBoxConfig(name, cfg)}
        />
      );
    }
    return (
      <HiveTypeConfig hiveType={hiveType} config={formData.hive_config} updateConfig={updateConfig} />
    );
  };


  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto mx-4 sm:mx-auto" data-testid="add-hive-dialog">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-xl sm:text-2xl font-bold text-stone-800 font-heading">
            Add New Hive
          </DialogTitle>
          {trapData && (
            <div className="bg-lime-100 border border-lime-300 rounded-lg p-3 mt-2 text-sm">
              <p className="font-semibold text-lime-800">🪤 Creating from Swarm Trap: {trapData.name}</p>
              <p className="text-lime-700 text-xs mt-1">The trap will be marked as converted when you save this hive.</p>
            </div>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4 min-w-0 overflow-hidden">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="hive_number" className="text-stone-700 font-medium">
                Hive Number (ID)
              </Label>
              <Input
                id="hive_number"
                type="number"
                placeholder={`Suggested: ${getNextHiveNumber()}`}
                value={formData.hive_number}
                onChange={(e) => setFormData({ ...formData, hive_number: e.target.value })}
                data-testid="hive-number-input"
                className="h-12 bg-stone-50 border-stone-200 rounded-xl mt-1"
              />
            </div>
            <div>
              <Label htmlFor="nickname" className="text-stone-700 font-medium">
                Hive Nickname <span className="text-stone-400 text-sm">(optional)</span>
              </Label>
              <Input
                id="nickname"
                type="text"
                placeholder="e.g., Queen Bee, Sunny, The Swarm"
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                data-testid="hive-nickname-input"
                className="h-12 bg-stone-50 border-stone-200 rounded-xl mt-1"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="yard" className="text-stone-700 font-medium">
              Select Apiary *
            </Label>
            <Select
              value={formData.yard}
              onValueChange={(value) => setFormData({ ...formData, yard: value })}
            >
              <SelectTrigger className="h-12 bg-stone-50 border-stone-200 rounded-xl mt-1" data-testid="apiary-select">
                <SelectValue placeholder="Choose a yard" />
              </SelectTrigger>
              <SelectContent>
                {yards.map((yard) => (
                  <SelectItem key={yard.id} value={yard.name}>
                    {yard.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Saved Templates - Quick Apply */}
          {hiveTemplates.length > 0 && (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
              <Label className="text-purple-800 font-medium text-base mb-2 block">
                ⭐ Saved Templates
              </Label>
              <p className="text-xs text-purple-600 mb-3">Apply a pre-saved configuration with one click</p>
              <div className="flex flex-wrap gap-2">
                {hiveTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => applyTemplate(template)}
                    className="px-3 py-2 bg-white border border-purple-200 rounded-lg text-sm font-medium text-purple-700 hover:bg-purple-100 hover:border-purple-300 transition-colors flex items-center gap-2"
                  >
                    <span>{template.icon || '📦'}</span>
                    <span>{template.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Copy Configuration from Existing Hive */}
          {existingHives && existingHives.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <Label className="text-blue-800 font-medium text-base mb-2 block">
                📋 Copy Configuration from Existing Hive
              </Label>
              <p className="text-xs text-blue-600 mb-3">Quickly duplicate another hive&apos;s box/frame setup</p>
              <div className="flex gap-2">
                <Select
                  value={copiedFromHive || ''}
                  onValueChange={(hiveId) => {
                    if (hiveId === '__clear__') {
                      setCopiedFromHive('');
                      setHiveType('');
                      setFormData(prev => ({ ...prev, hive_config: {} }));
                      toast.success('Copied configuration cleared');
                      return;
                    }
                    const sourceHive = existingHives.find(h => h.id === hiveId);
                    if (sourceHive) {
                      setCopiedFromHive(hiveId);
                      const stack = sourceHive.hive_stack || {};
                      const sourceType = (stack.hive_type || '').toLowerCase();
                    
                    // Set hive type
                    if (sourceType.includes('langstroth')) {
                      setHiveType('langstroth');
                      setFormData(prev => ({
                        ...prev,
                        hive_config: {
                          ...prev.hive_config,
                          langstroth_deep_brood_size: stack.deep_brood_size || '10',
                          langstroth_deep_brood_qty: stack.deep_brood_qty || 0,
                          langstroth_deep_super_size: stack.deep_super_size || '10',
                          langstroth_deep_super_qty: stack.deep_super_qty || 0,
                          langstroth_ideal_super_size: stack.ideal_super_size || '10',
                          langstroth_ideal_super_qty: stack.ideal_super_qty || 0,
                        }
                      }));
                    } else if (sourceType === 'horizontal' || sourceType.includes('top bar') || sourceType.includes('long')) {
                      setHiveType('horizontal');
                      setFormData(prev => ({
                        ...prev,
                        hive_config: {
                          ...prev.hive_config,
                          horizontal_frames: stack.horizontal_hive_frames || '20',
                          horizontal_entrances: stack.horizontal_hive_entrances || '1',
                          horizontal_colonies: stack.horizontal_hive_colonies || '1',
                        }
                      }));
                    } else if (sourceType.includes('flow')) {
                      setHiveType('flow');
                      setFormData(prev => ({
                        ...prev,
                        hive_config: {
                          ...prev.hive_config,
                          flow_deep_brood_size: stack.flow_hive_deep_brood_size || '8',
                          flow_deep_brood_qty: stack.flow_hive_deep_brood_qty || 1,
                          flow_super_size: stack.flow_hive_super_size || '6',
                          flow_super_qty: stack.flow_hive_super_qty || 0,
                        }
                      }));
                    } else if (sourceHive.box_config && Object.keys(sourceHive.box_config).length > 0) {
                      // Custom stackable hive with box_config
                      setHiveType('langstroth');
                      const boxCfg = sourceHive.box_config;
                      setFormData(prev => ({
                        ...prev,
                        hive_config: {
                          ...prev.hive_config,
                          langstroth_deep_brood_size: boxCfg['Deep Brood']?.size || '10',
                          langstroth_deep_brood_qty: boxCfg['Deep Brood']?.qty || 0,
                          langstroth_deep_super_size: boxCfg['Deep Super']?.size || '10',
                          langstroth_deep_super_qty: boxCfg['Deep Super']?.qty || 0,
                          langstroth_ideal_super_size: boxCfg['Ideal Super']?.size || '10',
                          langstroth_ideal_super_qty: boxCfg['Ideal Super']?.qty || 0,
                        }
                      }));
                    }
                    
                    toast.success(`Configuration copied from Hive ${sourceHive.hive_number}`);
                  }
                }}
              >
                <SelectTrigger className="h-12 bg-white border-blue-300 rounded-xl">
                  <SelectValue placeholder="Select a hive to copy from..." />
                </SelectTrigger>
                <SelectContent>
                  {copiedFromHive && (
                    <SelectItem value="__clear__" className="text-red-600 text-base py-2">
                      ✕ Clear copied configuration
                    </SelectItem>
                  )}
                  {existingHives.map((h) => (
                    <SelectItem key={h.id} value={h.id} className="text-base py-2">
                      Hive {h.hive_number} {h.nickname ? `(${h.nickname})` : ''} - {h.hive_stack?.hive_type || 'Unknown type'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
            </div>
          )}

          {/* Hive Type Dropdown - NEW DESIGN */}
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
            <Label className="text-stone-800 font-bold text-base mb-3 block">
              🏠 Hive Type *
            </Label>
            <Select
              value={hiveType}
              onValueChange={(value) => setHiveType(value)}
            >
              <SelectTrigger className="h-14 bg-white border-amber-300 rounded-xl text-lg font-medium">
                <SelectValue placeholder="Select hive type..." />
              </SelectTrigger>
              <SelectContent>
                {/* Admin-configured hive types (from API) */}
                {adminHiveTypes.length > 0 ? (
                  adminHiveTypes.map((type) => (
                    <SelectItem key={type.id || type.name} value={type.name?.toLowerCase().replace(/\s+/g, '_')} className="text-base py-3">
                      {type.name}
                    </SelectItem>
                  ))
                ) : (
                  /* Fallback to hardcoded if API didn't load */
                  HIVE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-base py-3">
                      {type.label}
                    </SelectItem>
                  ))
                )}
                {/* User custom hive types */}
                {userHiveTypes.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-xs text-stone-400 border-t mt-1 pt-1">My Custom Types</div>
                    {userHiveTypes.map((type) => (
                      <SelectItem key={type.id} value={`custom_${type.id}`} className="text-base py-3">
                        {type.name}
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
            
            {/* Dynamic Hive Configuration */}
            {renderHiveConfig()}
            
            {/* Save as Template Button */}
            {hiveType && (
              <div className="mt-4 pt-4 border-t border-amber-200">
                {!showSaveTemplate ? (
                  <button
                    type="button"
                    onClick={() => setShowSaveTemplate(true)}
                    className="text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center gap-2"
                  >
                    <span>💾</span>
                    <span>Save this configuration as a template</span>
                  </button>
                ) : (
                  <div className="flex gap-2 items-center">
                    <Input
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="Template name..."
                      className="flex-1 h-10"
                    />
                    <button
                      type="button"
                      onClick={saveAsTemplate}
                      className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }}
                      className="px-3 py-2 bg-stone-200 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-300"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Frame Types Selection - shown when hive type is selected */}
          {hiveType && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="frame-types-section">
              <Label className="text-stone-800 font-bold text-base mb-2 block">
                🖼️ Frame Types
              </Label>
              <p className="text-xs text-gray-600 mb-3">Select frame types used in this hive</p>
              <div className="flex flex-wrap gap-2 mb-3">
                <Toggle
                  pressed={allFramesSelected}
                  onPressedChange={handleAllFramesToggle}
                  className="h-9 px-3 data-[state=on]:bg-blue-600 data-[state=on]:text-white border border-blue-300 text-sm"
                >
                  All
                </Toggle>
                {getAvailableFrameTypes().map((frameType) => (
                  <Toggle
                    key={frameType}
                    pressed={selectedFrameTypes.includes(frameType)}
                    onPressedChange={() => handleFrameTypeToggle(frameType)}
                    className="h-9 px-3 data-[state=on]:bg-blue-600 data-[state=on]:text-white border border-blue-300 text-sm"
                  >
                    {frameType}
                  </Toggle>
                ))}
              </div>
              {selectedFrameTypes.length > 0 && (
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-blue-200">
                  {selectedFrameTypes.map((frameType) => (
                    <div key={frameType}>
                      <Label className="text-xs text-blue-700"># of {frameType}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={frameQuantities[frameType] || 0}
                        onChange={(e) => setFrameQuantities({
                          ...frameQuantities,
                          [frameType]: parseInt(e.target.value) || 0
                        })}
                        className="h-9 mt-1 bg-white text-sm"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Foundation Types Selection */}
          {hiveType && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4" data-testid="foundation-types-section">
              <Label className="text-stone-800 font-bold text-base mb-2 block">
                🧱 Foundation Types
              </Label>
              <p className="text-xs text-gray-600 mb-3">Select foundation types used in this hive</p>
              <div className="flex flex-wrap gap-2 mb-3">
                <Toggle
                  pressed={allFoundationsSelected}
                  onPressedChange={handleAllFoundationsToggle}
                  className="h-9 px-3 data-[state=on]:bg-purple-600 data-[state=on]:text-white border border-purple-300 text-sm"
                >
                  All
                </Toggle>
                {getAvailableFoundationTypes().map((foundationType) => (
                  <Toggle
                    key={foundationType}
                    pressed={selectedFoundationTypes.includes(foundationType)}
                    onPressedChange={() => handleFoundationTypeToggle(foundationType)}
                    className="h-9 px-3 data-[state=on]:bg-purple-600 data-[state=on]:text-white border border-purple-300 text-sm"
                  >
                    {foundationType}
                  </Toggle>
                ))}
              </div>
              {selectedFoundationTypes.length > 0 && (
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-purple-200">
                  {selectedFoundationTypes.map((foundationType) => (
                    <div key={foundationType}>
                      <Label className="text-xs text-purple-700"># of {foundationType}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={foundationQuantities[foundationType] || 0}
                        onChange={(e) => setFoundationQuantities({
                          ...foundationQuantities,
                          [foundationType]: parseInt(e.target.value) || 0
                        })}
                        className="h-9 mt-1 bg-white text-sm"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Hive Components - collapsible */}
          {hiveType && (
            <div className="border border-stone-200 rounded-xl overflow-hidden" data-testid="hive-components-section">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, _componentsOpen: !prev._componentsOpen }))}
                className="w-full flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition-colors"
              >
                <span className="font-bold text-stone-800 text-base">📦 Hive Components</span>
                {formData._componentsOpen ? <ChevronUp className="h-5 w-5 text-stone-400" /> : <ChevronDown className="h-5 w-5 text-stone-400" />}
              </button>
              {formData._componentsOpen && (
                <div className="p-4 space-y-3">
                  {renderDynamicComponentSections()}
                </div>
              )}
            </div>
          )}

          {/* Bee Variety */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <Label className="text-stone-700 font-medium text-base mb-3 block">
              🐝 Bee Variety
            </Label>
            <Select
              value={formData.bee_variety}
              onValueChange={(value) => setFormData({ ...formData, bee_variety: value })}
            >
              <SelectTrigger className="h-12">
                <SelectValue placeholder="Select bee variety" />
              </SelectTrigger>
              <SelectContent>
                {adminBeeVarieties.map((variety) => (
                  <SelectItem key={variety.id} value={variety.name}>
                    {variety.name}
                    {variety.temperament && <span className="text-stone-400 ml-2">({variety.temperament})</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Origin Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <Label className="text-stone-700 font-medium text-base mb-3 block">
              📦 Origin (Optional)
            </Label>
            <div className="space-y-3">
              <div>
                <Label className="text-stone-600 text-sm">Received as</Label>
                <Select
                  value={formData.origin_type}
                  onValueChange={(value) => {
                    const originConfig = adminOriginTypes.find(o => o.name === value);
                    setFormData({ 
                      ...formData, 
                      origin_type: value, 
                      swarm_source: '', 
                      swarm_trap_id: '',
                      origin_seller: originConfig?.requires_seller ? formData.origin_seller : ''
                    });
                  }}
                >
                  <SelectTrigger className="h-12 mt-1">
                    <SelectValue placeholder="Select origin type" />
                  </SelectTrigger>
                  <SelectContent>
                    {adminOriginTypes.map((type) => (
                      <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Show seller field if origin type requires it */}
              {formData.origin_type && (() => {
                const originConfig = adminOriginTypes.find(o => o.name === formData.origin_type);
                return originConfig?.requires_seller;
              })() && (
                <div>
                  <Label className="text-stone-600 text-sm">From (Seller/Source)</Label>
                  <Input
                    value={formData.origin_seller || ''}
                    onChange={(e) => setFormData({ ...formData, origin_seller: e.target.value })}
                    placeholder="Seller or source name"
                    className="h-12 mt-1"
                  />
                </div>
              )}
              
              {/* Show price field if origin type requires it */}
              {formData.origin_type && (() => {
                const originConfig = adminOriginTypes.find(o => o.name === formData.origin_type);
                return originConfig?.requires_price;
              })() && (
                <div>
                  <Label className="text-stone-600 text-sm">Purchase Price ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.origin_price || ''}
                    onChange={(e) => setFormData({ ...formData, origin_price: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    className="h-12 mt-1"
                  />
                </div>
              )}
              
              {/* Show date field if origin type requires it */}
              {formData.origin_type && (() => {
                const originConfig = adminOriginTypes.find(o => o.name === formData.origin_type);
                return originConfig?.requires_date;
              })() && (
                <div>
                  <Label className="text-stone-600 text-sm">Date Acquired</Label>
                  <Input
                    type="date"
                    value={formData.origin_date || ''}
                    onChange={(e) => setFormData({ ...formData, origin_date: e.target.value })}
                    className="h-12 mt-1"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Is this a Split? */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <Label className="text-stone-700 font-medium text-base mb-3 block">
              ✂️ Is this hive from a Split?
            </Label>
            <div className="flex gap-3">
              <Toggle
                pressed={isSplit}
                onPressedChange={(pressed) => {
                  setIsSplit(pressed);
                  setFormData({ ...formData, is_split: pressed });
                  if (!pressed) {
                    setSplitFromHive('');
                    setSplitType('');
                    setSplitTypeOther('');
                    setFormData({ ...formData, is_split: false, split_from_hive: '', split_type: '', split_type_other: '' });
                  }
                }}
                className="h-12 px-6 data-[state=on]:bg-emerald-600 data-[state=on]:text-white font-bold"
              >
                Yes, from Split
              </Toggle>
              <Toggle
                pressed={!isSplit}
                onPressedChange={() => {
                  setIsSplit(false);
                  setSplitFromHive('');
                  setSplitType('');
                  setSplitTypeOther('');
                  setFormData({ ...formData, is_split: false, split_from_hive: '', split_type: '', split_type_other: '' });
                }}
                className="h-12 px-6 data-[state=on]:bg-stone-600 data-[state=on]:text-white font-bold"
              >
                No
              </Toggle>
            </div>
            {isSplit && (
              <div className="mt-4 space-y-4">
                <div>
                  <Label className="text-stone-600 text-sm">Split from which hive?</Label>
                  <Select
                    value={splitFromHive}
                    onValueChange={(value) => {
                      setSplitFromHive(value);
                      setFormData({ ...formData, split_from_hive: value });
                    }}
                  >
                    <SelectTrigger className="h-12 mt-1">
                      <SelectValue placeholder="Select parent hive">
                        {splitFromHive && (
                          (() => {
                            const parentHive = existingHives?.find(h => h.id === splitFromHive);
                            return parentHive ? `Hive ${parentHive.hive_number} (${parentHive.yard || 'No yard'})` : splitFromHive;
                          })()
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {existingHives?.map((hive) => (
                        <SelectItem key={hive.id} value={hive.id}>
                          Hive {hive.hive_number} ({hive.yard || 'No yard'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-stone-600 text-sm mb-2 block">Split Type</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Toggle
                      pressed={splitType === 'walk-away'}
                      onPressedChange={() => {
                        setSplitType('walk-away');
                        setSplitTypeOther('');
                        setFormData({ ...formData, split_type: 'walk-away', split_type_other: '' });
                      }}
                      className="h-12 data-[state=on]:bg-emerald-600 data-[state=on]:text-white"
                    >
                      Walk-away Split
                    </Toggle>
                    <Toggle
                      pressed={splitType === 'queen-right'}
                      onPressedChange={() => {
                        setSplitType('queen-right');
                        setSplitTypeOther('');
                        setFormData({ ...formData, split_type: 'queen-right', split_type_other: '' });
                      }}
                      className="h-12 data-[state=on]:bg-emerald-600 data-[state=on]:text-white"
                    >
                      Queen Right
                    </Toggle>
                    <Toggle
                      pressed={splitType === 'other'}
                      onPressedChange={() => {
                        setSplitType('other');
                        setFormData({ ...formData, split_type: 'other' });
                      }}
                      className="h-12 data-[state=on]:bg-emerald-600 data-[state=on]:text-white"
                    >
                      Other
                    </Toggle>
                  </div>
                  {splitType === 'other' && (
                    <Input
                      className="mt-2 h-12"
                      placeholder="Describe the split type..."
                      value={splitTypeOther}
                      onChange={(e) => {
                        setSplitTypeOther(e.target.value);
                        setFormData({ ...formData, split_type_other: e.target.value });
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Hive Image */}
          <div>
            <Label className="text-stone-700 font-medium mb-3 block">
              🖼️ Hive Background Image (Optional)
            </Label>
            <div className="flex items-center gap-4">
              {formData.hive_image ? (
                <div className="relative">
                  <img
                    src={`${API}/uploads/${formData.hive_image}`}
                    alt="Hive Background"
                    className="w-24 h-16 rounded-lg object-cover border-2 border-stone-200"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, hive_image: '' })}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-16 rounded-lg bg-stone-200 flex items-center justify-center border-2 border-stone-300">
                  <span className="text-stone-500 text-xs">No Image</span>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="add-hive-camera-capture"
                  disabled={uploadingImage}
                />
                <Button
                  type="button"
                  variant="secondary"
                  disabled={uploadingImage}
                  onClick={() => document.getElementById('add-hive-camera-capture').click()}
                  className="h-10 px-3 bg-purple-100 hover:bg-purple-200 text-purple-700"
                >
                  <Camera className="mr-1 h-4 w-4" />
                  Camera
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="add-hive-image-upload"
                  disabled={uploadingImage}
                />
                <Button
                  type="button"
                  variant="secondary"
                  disabled={uploadingImage}
                  onClick={() => document.getElementById('add-hive-image-upload').click()}
                  className="h-10 px-3"
                >
                  <Upload className="mr-1 h-4 w-4" />
                  {uploadingImage ? '...' : 'Upload'}
                </Button>
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <PricingSection
            pricing={hivePricing}
            onPricingChange={setHivePricing}
            showOriginPrice={true}
            originPrice={originPrice}
            onOriginPriceChange={setOriginPrice}
            stackConfig={formData.hive_config}
            components={formData.hive_components}
            defaultPrices={defaultPrices}
            frameTypes={selectedFrameTypes}
            frameQuantities={frameQuantities}
            foundationTypes={selectedFoundationTypes}
            foundationQuantities={foundationQuantities}
            entityName={`Hive ${formData.hive_number || 'New'}`}
          />

          {/* Notes */}
          <div>
            <Label htmlFor="notes" className="text-stone-700 font-medium">
              Notes
            </Label>
            <Textarea
              id="notes"
              placeholder="Any additional information..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              data-testid="notes-input"
              className="bg-stone-50 border-stone-200 rounded-xl mt-1 min-h-16"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              data-testid="cancel-btn"
              className="flex-1 h-11 sm:h-12 rounded-full font-semibold text-sm sm:text-base"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-hive-btn"
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white h-11 sm:h-12 rounded-full font-bold text-sm sm:text-base"
            >
              Add Hive
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Reusable Stack Row Component for box configurations

export default AddHiveDialog;
